import {
  useFeatureFlagService,
  type FeatureFlags,
} from 'utils/useFeatureFlagService'
import LoadingScreen from '../components/Layout/LoadingScreen'
import { Navigate } from 'react-router-dom'

/**
 * Gate a route on feature flags. Provide exactly one of:
 *   - `featureFlag`: a single flag key (enabled when that flag is true).
 *   - `predicate`: a function over all flags (enabled when it returns true).
 *     Use for OR / compound conditions, e.g. `isHomepageEnabled`.
 * A disabled route redirects to /dashboard.
 */
interface FeatureProtectedRouteProps {
  featureFlag?: keyof ReturnType<typeof useFeatureFlagService>
  predicate?: (flags: FeatureFlags) => boolean
  children: React.ReactNode
}

export default function FeatureProtectedRoute({
  featureFlag,
  predicate,
  children,
}: FeatureProtectedRouteProps) {
  const featureFlags = useFeatureFlagService()

  if (featureFlags.isLoading) {
    return <LoadingScreen />
  }

  const isEnabled = predicate
    ? predicate(featureFlags)
    : featureFlag
      ? featureFlags[featureFlag]
      : false

  if (!isEnabled) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
